import { useState } from 'react';
import { withNamespaces } from 'react-i18next';
import { toast } from 'react-toastify';
import { Alert, Col, Row } from 'reactstrap';
import { AvForm } from 'availity-reactstrap-validation';

import { isFeatureEnabledForActiveModule } from 'src/helpers/module_helper';
import useMailerDriver from 'src/modules/mailer/hooks/useMailerDriver';

import DangerZone from './components/danger-zone';
import EntraSetupDrawer from './components/entra-setup-drawer';
import FormActions, {
    MAILER_SETTINGS_FORM_ID,
} from './components/form-actions';
import InboxSync from './components/inbox-sync';
import MailboxDetails from './components/mailbox-details';
import SendingSettings from './components/sending-settings';
import StatusSummary from './components/status-summary';
import TestStatusAlert from './components/test-status-alert';
import DisconnectMicrosoftModal from './components/microsoft-connection/components/DisconnectMicrosoftModal';
import MailboxChangeModal from './components/mailbox-change-modal';
import ToggleActivationModal from './components/toggle-activation-modal';
import useMicrosoftOAuth from './components/microsoft-connection/hooks/useMicrosoftOAuth';
import useEmailSettings from './hooks/useEmailSettings';
import { getMicrosoftOAuthRedirectUri } from './helpers';
import './email-settings.scss';

const EmailSettings = ({
    t,
}) => {
    const [showSetupGuide, setShowSetupGuide] = useState(false);
    const {
        formRef,
        formKey,
        defaultSettings,
        provider,
        presets,
        imapSyncEnabled,
        setImapSyncEnabled,
        incomingSameAsSmtp,
        setIncomingSameAsSmtp,
        showAdvanced,
        testStatus,
        testMessage,
        active,
        showToggleActivationModal,
        setShowToggleActivationModal,
        isFetching,
        isMicrosoft,
        isCustom,
        isFirstTime,
        secretRequired,
        incomingSecretRequired,
        submitMutation,
        toggleActivationMutation,
        testConnectionMutation,
        handleProviderChange,
        handleEmailAddressChange,
        handleSubmit,
        handleTestConnection,
        mailboxChange,
        handleCancelMailboxChange,
        handleConfirmMailboxChange,
        refreshMailerSettings,
    } = useEmailSettings({ t });
    const { apiRoutes } = useMailerDriver();

    const mailerEnabled = isFeatureEnabledForActiveModule('mailer');
    const {
        isConnecting,
        isDisconnecting,
        showDisconnectModal,
        setShowDisconnectModal,
        handleConnect,
        handleReconnect,
        handleConfirmDisconnect,
    } = useMicrosoftOAuth({
        t,
        enabled: mailerEnabled,
        onSettingsRefresh: refreshMailerSettings,
    });

    if (!mailerEnabled) {
        return (
            <Alert color="warning">
                {t('Please upgrade your plan to access this feature')}.
            </Alert>
        );
    }

    const redirectUri = getMicrosoftOAuthRedirectUri(
        defaultSettings?.oauthRedirectUri,
        apiRoutes?.API_BASE_URL
    );

    const handleCopyRedirectUri = () => {
        if (!redirectUri) {
            toast.error(t('Redirect URI is not configured on the server.'));
            return;
        }

        navigator.clipboard
            .writeText(redirectUri)
            .then(() => toast.success(t('Redirect URI copied to clipboard')))
            .catch(() => toast.error(t('Failed to copy Redirect URI')));
    };

    return (
        <Row className="mailer-settings">
            <Col sm="12" md="12" xl="12">
                <StatusSummary
                    active={active}
                    providerLabel={t(presets[provider]?.label || provider)}
                    emailAddress={defaultSettings?.emailAddress}
                    imapSyncEnabled={imapSyncEnabled}
                    isMicrosoft={isMicrosoft}
                    oauthConnected={!!defaultSettings?.oauthConnected}
                />

                <TestStatusAlert
                    testStatus={testStatus}
                    testMessage={testMessage}
                />

                <AvForm
                    id={MAILER_SETTINGS_FORM_ID}
                    key={formKey}
                    className="needs-validation"
                    onValidSubmit={handleSubmit}
                    ref={formRef}
                >
                    <MailboxDetails
                        isFetching={isFetching}
                        provider={provider}
                        presets={presets}
                        defaultSettings={defaultSettings}
                        isMicrosoft={isMicrosoft}
                        onProviderChange={handleProviderChange}
                        onEmailAddressChange={handleEmailAddressChange}
                    />

                    <SendingSettings
                        isFetching={isFetching}
                        defaultSettings={defaultSettings}
                        provider={provider}
                        isMicrosoft={isMicrosoft}
                        isCustom={isCustom}
                        secretRequired={secretRequired}
                        forceAdvanced={isCustom || showAdvanced}
                    />

                    <InboxSync
                        isFetching={isFetching}
                        defaultSettings={defaultSettings}
                        isMicrosoft={isMicrosoft}
                        isCustom={isCustom}
                        imapSyncEnabled={imapSyncEnabled}
                        onImapSyncChange={setImapSyncEnabled}
                        incomingSameAsSmtp={incomingSameAsSmtp}
                        onIncomingSameAsSmtpChange={setIncomingSameAsSmtp}
                        incomingSecretRequired={incomingSecretRequired}
                        hasSavedIncomingSecret={!isFirstTime}
                        forceAdvanced={isCustom || showAdvanced}
                        redirectUri={redirectUri}
                        onCopyRedirectUri={handleCopyRedirectUri}
                        onOpenSetupGuide={() => setShowSetupGuide(true)}
                        isConnecting={isConnecting}
                        isDisconnecting={isDisconnecting}
                        onConnectClick={handleConnect}
                        onReconnectClick={handleReconnect}
                        onDisconnectClick={() => setShowDisconnectModal(true)}
                    />

                    <DangerZone
                        isFetching={isFetching}
                        isFirstTime={isFirstTime}
                        active={active}
                        isSaving={submitMutation.isLoading}
                        onToggleActivation={() =>
                            setShowToggleActivationModal(active ? 'deactivate' : 'activate')
                        }
                    />

                    <FormActions
                        isFetching={isFetching}
                        isFirstTime={isFirstTime}
                        submitMutation={submitMutation}
                        testConnectionMutation={testConnectionMutation}
                        onTestConnection={handleTestConnection}
                    />
                </AvForm>

                <EntraSetupDrawer
                    isOpen={showSetupGuide}
                    redirectUri={redirectUri}
                    onClose={() => setShowSetupGuide(false)}
                    onCopyRedirectUri={handleCopyRedirectUri}
                />

                <MailboxChangeModal
                    isOpen={!!mailboxChange}
                    toggle={handleCancelMailboxChange}
                    isLoading={submitMutation.isLoading}
                    mailboxChange={mailboxChange}
                    showMicrosoftReconnectHint={
                        isMicrosoft || !!defaultSettings?.oauthEntraConfigured
                    }
                    onConfirm={handleConfirmMailboxChange}
                />

                <DisconnectMicrosoftModal
                    isOpen={showDisconnectModal}
                    toggle={() => setShowDisconnectModal(false)}
                    isLoading={isDisconnecting}
                    onConfirm={handleConfirmDisconnect}
                />

                <ToggleActivationModal
                    isOpen={showToggleActivationModal !== false}
                    toggle={() => {
                        setShowToggleActivationModal(false);
                    }}
                    toggleActivationMutation={toggleActivationMutation}
                    action={showToggleActivationModal}
                />
            </Col>
        </Row>
    );
};

export default withNamespaces()(EmailSettings);
